import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const UploadTipsPanel = ({ className = "" }) => {
  const [activeTab, setActiveTab] = useState('lighting');

  const tips = {
    lighting: {
      title: 'Optimal Lighting',
      icon: 'Sun',
      content: [
        {
          tip: 'Use natural daylight when possible',
          description: 'Outdoor lighting or near a large window provides the best color accuracy for component identification.'
        },
        {
          tip: 'Avoid harsh shadows',
          description: 'Position your bike so components are evenly lit without deep shadows hiding details.'
        },
        {
          tip: 'No direct flash',
          description: 'Camera flash can create glare on metal components and wash out important details.'
        }
      ],
      example: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=300&fit=crop'
    },
    angles: {
      title: 'Best Angles',
      icon: 'RotateCcw',
      content: [
        {
          tip: 'Side profile (drive side)',
          description: 'Capture the complete bike from the chain side to show drivetrain components clearly.'
        },
        {
          tip: 'Cockpit area',
          description: 'Close-up of handlebars, stem, and brake/shift levers for control component analysis.'
        },
        {
          tip: 'Rear derailleur detail',
          description: 'Focus on the rear derailleur and cassette for accurate drivetrain assessment.'
        }
      ],
      example: 'https://images.unsplash.com/photo-1571068316344-75bc76f77890?w=400&h=300&fit=crop'
    },
    composition: {
      title: 'Photo Composition',
      icon: 'Camera',
      content: [
        {
          tip: 'Fill the frame',
          description: 'Make sure your bike takes up most of the photo frame for better component visibility.'
        },
        {
          tip: 'Clean background',
          description: 'Use a simple, uncluttered background to help our AI focus on bike components.'
        },
        {
          tip: 'Stable positioning',
          description: 'Use a bike stand or lean against a wall to keep the bike steady and components visible.'
        }
      ],
      example: 'https://images.unsplash.com/photo-1544191696-15693072b5a7?w=400&h=300&fit=crop'
    }
  };

  const tabs = Object.keys(tips);

  return (
    <div className={`bg-card border border-border rounded-lg p-6 ${className}`}>
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-foreground mb-2">
          Photo Upload Tips
        </h3>
        <p className="text-sm text-muted-foreground">
          Follow these guidelines to get the most accurate analysis and recommendations for your bike.
        </p>
      </div>
      {/* Tab Navigation */}
      <div className="flex space-x-1 mb-6 bg-muted p-1 rounded-lg">
        {tabs?.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex-1 flex items-center justify-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 interactive-scale ${
              activeTab === tab
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name={tips?.[tab]?.icon} size={16} />
            <span className="hidden sm:inline">{tips?.[tab]?.title}</span>
          </button>
        ))}
      </div>
      {/* Active Tab Content */}
      <div className="space-y-6">
        {/* Example Image */}
        <div className="aspect-video bg-muted rounded-lg overflow-hidden">
          <Image
            src={tips?.[activeTab]?.example}
            alt={`${tips?.[activeTab]?.title} example`}
            className="w-full h-full object-cover"
          />
        </div>

        {/* Tips List */}
        <div className="space-y-4">
          {tips?.[activeTab]?.content?.map((item, index) => (
            <div key={index} className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-6 h-6 bg-accent/10 rounded-full flex items-center justify-center mt-0.5">
                <span className="text-xs font-semibold text-accent">{index + 1}</span>
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-medium text-foreground mb-1">
                  {item?.tip}
                </h4>
                <p className="text-sm text-muted-foreground">
                  {item?.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Quick Reference */}
        <div className="mt-6 p-4 bg-accent/5 border border-accent/20 rounded-lg">
          <div className="flex items-start space-x-2">
            <Icon name="Zap" size={16} color="var(--color-accent)" className="mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              <p className="text-accent font-medium mb-1">Quick Checklist</p>
              <ul className="text-muted-foreground space-y-1">
                <li>✓ Good lighting conditions</li>
                <li>✓ Multiple angles captured</li>
                <li>✓ Components clearly visible</li>
                <li>✓ Clean, uncluttered background</li>
                <li>✓ High resolution images</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadTipsPanel;